import React, { useState } from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { PageHeader, PageBody, EmptyState } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

const STATUSES = ["lead", "scheduled", "in_progress", "completed", "invoiced"];
const SOURCES = ["referral", "website", "yard_sign", "google", "facebook", "repeat", "other"];

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function Projects() {
  const { t, lang } = useI18n();
  const { data: projects = [] } = useSWR("/projects", fetcher);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", customer_name: "", address: "", phone: "", email: "", status: "lead", lead_source: "referral", lead_value: 0, next_follow_up: "", notes: "" });

  const submit = async () => {
    if (!form.name || !form.customer_name || !form.address) {
      toast.error(lang === "fr" ? "Nom, client et adresse requis" : "Name, customer and address are required");
      return;
    }
    try {
      await api.post("/projects", { ...form, lead_value: Number(form.lead_value || 0) });
      toast.success(lang === "fr" ? "Projet créé" : "Project created");
      setOpen(false);
      setForm({ name: "", customer_name: "", address: "", phone: "", email: "", status: "lead", lead_source: "referral", lead_value: 0, next_follow_up: "", notes: "" });
      mutate("/projects");
    } catch (e) { toast.error("Failed to create"); }
  };

  return (
    <div data-testid="projects-page">
      <PageHeader
        eyebrow="// 02 / Projects"
        title={t("projects.title")}
        sub={t("projects.sub")}
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-project-button" className="rounded-none bg-zinc-900 hover:bg-orange-600 text-white uppercase font-bold tracking-wider text-xs h-11 px-5">
                <Plus size={14} className="mr-2" weight="bold" /> {t("projects.new")}
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-zinc-300 max-w-xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display font-black tracking-tight text-2xl">{t("projects.new")}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 mt-4">
                <Field label={lang === "fr" ? "Nom du projet" : "Project name"}><Input data-testid="project-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label={t("projects.customer")}><Input data-testid="project-customer-input" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="rounded-none" /></Field>
                  <Field label={t("common.status")}>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger data-testid="project-status-select" className="rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{t(`status.${s}`)}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                </div>
                <Field label={t("common.address")}><Input data-testid="project-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-none" /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label={t("common.phone")}><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none" /></Field>
                  <Field label={t("common.email")}><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none" /></Field>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Field label={t("projects.leadSource")}>
                    <Select value={form.lead_source} onValueChange={(v) => setForm({ ...form, lead_source: v })}>
                      <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s.replace("_", " ").toUpperCase()}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label={t("projects.leadValue")}><Input data-testid="project-value-input" type="number" value={form.lead_value} onChange={(e) => setForm({ ...form, lead_value: e.target.value })} className="rounded-none mono" /></Field>
                  <Field label={t("projects.followUp")}><Input data-testid="project-followup-input" type="date" value={form.next_follow_up} onChange={(e) => setForm({ ...form, next_follow_up: e.target.value })} className="rounded-none mono" /></Field>
                </div>
                <Field label={t("common.notes")}><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none" /></Field>
                <Button data-testid="project-save-button" onClick={submit} className="rounded-none bg-orange-600 hover:bg-orange-700 text-white uppercase font-bold tracking-wider w-full h-11">{t("common.save")}</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />
      <PageBody>
        {projects.length === 0 ? (
          <EmptyState title={t("projects.empty")} sub={t("projects.sub")} />
        ) : (
          <div className="border border-zinc-200">
            <div className="grid grid-cols-12 px-5 py-3 border-b border-zinc-200 label-eyebrow text-zinc-500 bg-zinc-50">
              <div className="col-span-3">{lang === "fr" ? "Projet" : "Project"}</div>
              <div className="col-span-2">{t("projects.customer")}</div>
              <div className="col-span-3">{t("common.address")}</div>
              <div className="col-span-1 text-right">$</div>
              <div className="col-span-1">{t("projects.followUp")}</div>
              <div className="col-span-2">{t("common.status")}</div>
            </div>
            {projects.map((p) => (
              <Link key={p.id} to={`/app/projects/${p.id}`} data-testid={`project-row-${p.id}`}
                className="grid grid-cols-12 px-5 py-4 border-b border-zinc-100 hover:bg-zinc-50 transition-colors items-center">
                <div className="col-span-3 font-semibold">{p.name}</div>
                <div className="col-span-2 text-zinc-700 text-sm">{p.customer_name}</div>
                <div className="col-span-3 text-zinc-500 text-sm truncate">{p.address}</div>
                <div className="col-span-1 text-right mono text-xs">{fmt$(p.lead_value)}</div>
                <div className="col-span-1 mono text-xs text-zinc-600">{p.next_follow_up || "—"}</div>
                <div className="col-span-2"><Badge status={p.status} /></div>
              </Link>
            ))}
          </div>
        )}
      </PageBody>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="label-eyebrow text-zinc-600 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

export function Badge({ status }) {
  const map = {
    lead: "bg-zinc-100 text-zinc-700 border-zinc-300",
    scheduled: "bg-blue-50 text-blue-700 border-blue-300",
    in_progress: "bg-orange-50 text-orange-700 border-orange-400",
    completed: "bg-green-50 text-green-700 border-green-300",
    invoiced: "bg-zinc-900 text-white border-zinc-900",
    draft: "bg-zinc-100 text-zinc-700 border-zinc-300",
    sent: "bg-blue-50 text-blue-700 border-blue-300",
    paid: "bg-green-50 text-green-700 border-green-300",
    accepted: "bg-green-50 text-green-700 border-green-300",
    declined: "bg-red-50 text-red-700 border-red-300",
    deposit_paid: "bg-green-700 text-white border-green-700",
  };
  return (
    <span className={`inline-block label-eyebrow px-2 py-1 border ${map[status] || map.lead}`}>{(status || "").replace("_", " ")}</span>
  );
}
